import ListingTypeEnum from '@models/enums/ListingTypeEnum';
import SiteEnum from '@models/enums/SiteEnum';
import { IFilterGroup } from '@models/IFilterGroup';
import { IHotspotCard } from '@models/IHotspotCard';
import { IImage } from '@models/IImage';
import { IInspirationCard } from '@models/IInspirationCard';
import { IListing } from '@models/IListing';
import AddToMyListButton from '@molecules/AddToMyListButton/AddToMyListButton';
import ListingWrapper from '@molecules/ListingWrapper/ListingWrapper';

type CategoryFilters = {
  readonly id: string;
  readonly label: string;
  readonly featuredInspiration?: IHotspotCard;
  readonly inspirationCard?: IInspirationCard;
  readonly image?: IImage;
}[];

interface Props extends IListing {
  readonly autoCompleteEndpoint: string;
  readonly categoryFilters: CategoryFilters;
  readonly language: string;
  readonly sortOrder: string;
}

const ProductListing = ({
  categoryFilters,
  autoCompleteEndpoint,
  searchEndpoint,
  language,
  sortOrder,
  filters,
  preAppliedFilters,
  ...props
}: Props) => {
  const flatPreAppliedFilters =
    preAppliedFilters?.flatMap((filter) =>
      filter.options.map((option) => ({
        id: filter.id,
        value: option.id,
        isEditable: option.isEditable,
      })),
    ) ?? [];
  const permanentFilters = flatPreAppliedFilters.filter(
    (option) => !option.isEditable,
  );

  const formattedFilters =
    permanentFilters.length > 0
      ? getFormattedFilters(filters, permanentFilters)
      : filters;

  const initialQueryParams = {
    sortOrder,
    epslanguage: language,
    ...convertToFilterObject(permanentFilters),
  };

  return (
    <ListingWrapper
      {...props}
      listingFilterParam="industries"
      listingFilters={categoryFilters.map((filters) => ({
        ...filters,
        filter: filters,
      }))}
      initialGridSize={100}
      searchEndpoint={`${searchEndpoint}/search`}
      autoCompleteEndpoint={`${autoCompleteEndpoint}?language=${language}&query`}
      customQueryParams={initialQueryParams}
      type={ListingTypeEnum.Product}
      WishlistButton={AddToMyListButton}
      gridSizeRange={[100]}
      site={SiteEnum.B2B}
      initialFilters={convertToFilterObject(flatPreAppliedFilters)}
      filters={formattedFilters}
      permanentFilters={permanentFilters.map((f) => f.value)}
    />
  );
};

// Any subfilters not a child of a permanent filter are removed from a filter group
const getFormattedFilters = (
  filters: IFilterGroup[],
  permanentFilters: {
    id: string;
    value: string;
    isEditable: boolean;
  }[],
): IFilterGroup[] => {
  const permanentIds = permanentFilters.map((p) => p.id);

  const formattedFilters: IFilterGroup[] = filters.map((f) => {
    if (!permanentIds.includes(f.id)) return f;

    const permanentFilterGroup = permanentFilters
      .filter((p) => p.id === f.id)
      .map((p) => p.value);

    const permanentFilterGroupOptions = f.options.filter((option) =>
      permanentFilterGroup.includes(option.id),
    );

    if (
      permanentFilterGroupOptions.length === 1 &&
      permanentFilterGroupOptions[0]?.subFilters?.options
    ) {
      // If there is only one subfiltergroup then raise it to the parent level
      return {
        ...f,
        id: permanentFilterGroupOptions[0].subFilters.id,
        options: permanentFilterGroupOptions[0].subFilters.options,
      };
    }

    return {
      ...f,
      options: permanentFilterGroupOptions,
    };
  });
  return formattedFilters;
};

const convertToFilterObject = (filters: { id: string; value: string }[]) =>
  filters.reduce((a: Record<string, string[]>, v) => {
    const preExistingField = a[v.id];
    if (preExistingField) {
      return { ...a, [v.id]: [...preExistingField, v.value] };
    }
    return { ...a, [v.id]: [v.value] };
  }, {});

export default ProductListing;
